/**
 * LEGACY_28D1 break semantics: production main 28d166931e
 * (`packages/utils/src/payroll/break-treatment.ts`, resolver hash
 * `break-treatment-v12-no-rule-and-salary-flat`), kept byte-for-byte below this
 * header so every rebuild-OFF tenant resolves breaks exactly as live does.
 *
 * Release ruling 28 Oct 2026 (gate-to-legacy, R71): a tenant whose payroll
 * rebuild is OFF must see 28d1 behaviour on every legacy-visible path. The
 * v13/20 Oct semantics live in `./break-treatment.ts` and apply only when a
 * server caller passes `breakSemantics: 'V13'` (resolved from
 * `payrollRebuildEnabled`). Do not edit the body; retire this module when the
 * legacy payroll path is retired.
 */
/* eslint-disable */
export type BreakTreatmentKind =
  | 'UNPAID'
  | 'PAID_COUNTS_AS_TIME_WORKED'
  | 'PAID_NOT_TIME_WORKED'
  | 'NO_BREAK';

export type BreakTreatmentStatus = 'NOT_APPLICABLE' | 'RESOLVED' | 'BLOCKED' | 'AMBIGUOUS';

const BREAK_TREATMENT_RESOLVER_HASH_VERSION = 'break-treatment-v12-no-rule-and-salary-flat';

export const DEFAULT_TIMESHEET_MISSING_BREAK_POLICY = 'RECORDED_ONLY' as const;

export type TimesheetMissingBreakPolicyMode =
  | typeof DEFAULT_TIMESHEET_MISSING_BREAK_POLICY
  | 'ASSUME_RULE_DEFAULT_UNLESS_EXPLICIT_NO_BREAK';

export interface TimesheetMissingBreakPolicyConfig {
  mode: TimesheetMissingBreakPolicyMode;
  effectiveFrom: string | null;
  configuredAt: string | null;
  configuredBy: string | null;
  reason: string | null;
}

export type TimesheetBreakMinutesProvenance =
  | 'APPROVED_FACT_NOT_TAKEN'
  | 'APPROVED_FACT_DURATION'
  | 'APPROVED_FACT_TAKEN_RULE_DEFAULT'
  | 'RECORDED_BREAK_MINUTES'
  | 'PROVIDER_POLICY_RULE_DEFAULT'
  | 'LEGACY_RECORDED_ZERO'
  | 'RESOLVER_RULE_DEFAULT'
  | 'SCHEDULED_BREAK_MINUTES'
  | 'RULE_NO_BREAK_REQUIRED'
  | 'UNRESOLVED'
  | 'NONE';

export type BreakTreatmentSourceKind =
  | 'ROSTER_SHIFT'
  | 'ROSTER_ASSIGNMENT'
  | 'TIMESHEET'
  | 'LEAVE_REQUEST';

export interface BreakTreatmentRuleBlock {
  id?: string | null;
  block_code: string;
  block_kind: string;
  title?: string | null;
  precedence?: number | null;
  automation_status?: string | null;
  required_fact_codes?: string[] | null;
  export_component_codes?: string[] | null;
  parameters?: Record<string, unknown> | null;
}

export interface BreakTreatmentFactInput {
  factCode: string;
  value: unknown;
  status?: string | null;
}

export interface BreakTreatmentSegment {
  segmentKind:
    | 'WORKED'
    | 'UNPAID_BREAK'
    | 'PAID_BREAK_COUNTS_AS_TIME_WORKED'
    | 'PAID_BREAK_NOT_TIME_WORKED';
  minutes: number;
  countsForPayroll: boolean;
  countsAsTimeWorked: boolean;
  countsForLeave: boolean;
  countsForOvertimeThreshold: boolean;
  countsForCareMinutes: boolean;
}

export interface BreakTreatmentSnapshotV1 {
  schemaVersion: 1;
  status: BreakTreatmentStatus;
  treatmentKind: BreakTreatmentKind;
  sourceKind: BreakTreatmentSourceKind;
  ruleBlockId: string | null;
  ruleBlockCode: string | null;
  /** Applicable rule proving this worked span has not reached its break trigger. */
  noBreakRequiredRuleBlockId?: string | null;
  ruleHash: string;
  scheduledBreakMinutes: number;
  actualBreakMinutes: number;
  /** Why the resolver used these actual break minutes. Added without changing schemaVersion 1. */
  actualBreakMinutesProvenance?: TimesheetBreakMinutesProvenance;
  /** Numeric legacy storage is not evidence of a confirmed zero. */
  breakMinutesStatus?: 'UNRESOLVED';
  rawSpanMinutes: number;
  workedMinutes: number;
  payrollPaidBreakMinutes: number;
  payrollDeductibleBreakMinutes: number;
  ordinaryTimeWorkedBreakMinutes: number;
  leaveAccruableBreakMinutes: number;
  overtimeThresholdBreakMinutes: number;
  careMinuteExcludedBreakMinutes: number;
  requiredFactCodes: string[];
  suppliedFactCodes: string[];
  derivedFacts?: Array<{
    factCode: string;
    provenance:
      | 'DERIVED_FROM_RECORDED_BREAK_MINUTES'
      | 'DERIVED_FROM_PROVIDER_MISSING_BREAK_POLICY'
      | 'DERIVED_FROM_APPROVED_BREAK_TAKEN_FACT'
      | 'DERIVED_FROM_RULE_PAID_BREAK_ASSUMPTION';
  }>;
  missingFactCodes: string[];
  blockers: Array<{ code: string; message: string }>;
  segments: BreakTreatmentSegment[];
}

export interface ResolveBreakTreatmentInput {
  sourceKind: BreakTreatmentSourceKind;
  employeeCategory?: string | null;
  rawSpanMinutes: number;
  scheduledBreakMinutes: number | null | undefined;
  actualBreakMinutes?: number | null;
  actualBreakMinutesProvenance?: TimesheetBreakMinutesProvenance;
  /** The stored row value, before unknown punch provenance is recovered. */
  recordedBreakMinutes?: number | null;
  /** True only after a successful tenant-wide BREAK_TREATMENT/BREAKS read. */
  noTenantBreakRule?: boolean;
  ruleBlocks?: BreakTreatmentRuleBlock[] | null;
  facts?: BreakTreatmentFactInput[] | null;
  finalizationMode?: 'FORECAST' | 'FINAL';
  /**
   * The worker's LIVE payment method, never a stored marker.
   *
   * Break treatment exists to tell the award engine how to price a break. A
   * salary-flat worker never enters award classification — their ordinary pay is
   * materialised once per pay period by the salary-flat synthesiser — so no break
   * determination can change what they are paid. Supplying `SALARY_FLAT` here
   * resolves to NOT_APPLICABLE instead of demanding meal-break facts that cannot
   * affect the outcome.
   *
   * Safe to suppress because the snapshot has exactly one consumer: the award
   * engine reads `countsAsTimeWorked` (packages/utils/src/award-engines/aged-care.ts).
   * `countsForCareMinutes` and `countsForLeave` are computed but consumed nowhere —
   * care-minute reporting does not read this snapshot — so nothing outside payroll
   * loses information.
   */
  paymentMethod?: string | null;
}

function normalizedText(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function normalizedUpper(value: unknown): string | null {
  return normalizedText(value)?.toUpperCase() ?? null;
}

export function canonicalEmployeeCategory(value: unknown): string | null {
  const normalized = normalizedUpper(value);
  if (!normalized) return null;
  if (normalized === 'DAYWORKER' || normalized === 'DAY_WORKER') return 'DAYWORKER';
  if (
    normalized === 'SHIFTWORKER' ||
    normalized === 'SHIFT_WORKER' ||
    normalized === 'ROTATING_ROSTER' ||
    normalized === 'PERMANENT_NIGHT' ||
    normalized === 'PERMANENT_NIGHT_SHIFT'
  ) {
    return 'SHIFTWORKER';
  }
  return null;
}

function nonNegativeMinutes(value: unknown, fallback = 0): number {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.round(n));
}

function nonNegativeNumber(value: unknown, fallback = 100): number {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, n);
}

function recordText(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function validDateOnly(value: string | null): string | null {
  return value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
}

export function parseTimesheetMissingBreakPolicy(
  value: unknown,
): TimesheetMissingBreakPolicyConfig {
  const record =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  const configuredMode = normalizedUpper(record.mode);
  const mode: TimesheetMissingBreakPolicyMode =
    configuredMode === 'ASSUME_RULE_DEFAULT_UNLESS_EXPLICIT_NO_BREAK'
      ? configuredMode
      : DEFAULT_TIMESHEET_MISSING_BREAK_POLICY;
  return {
    mode,
    effectiveFrom: validDateOnly(recordText(record, 'effectiveFrom')),
    configuredAt: recordText(record, 'configuredAt'),
    configuredBy: recordText(record, 'configuredBy'),
    reason: recordText(record, 'reason'),
  };
}

export function timesheetMissingBreakPolicyApplies(
  policy: TimesheetMissingBreakPolicyConfig,
  eventDate: string,
): boolean {
  return (
    policy.mode === 'ASSUME_RULE_DEFAULT_UNLESS_EXPLICIT_NO_BREAK' &&
    (policy.effectiveFrom === null || eventDate >= policy.effectiveFrom)
  );
}

export interface ResolveTimesheetActualBreakInput {
  eventDate: string;
  storedBreakMinutes: number | null | undefined;
  /** Server-captured attendance or coordinator correction provenance. */
  sourceEvidence?: unknown;
  approvedMealBreakTaken?: boolean | null;
  approvedMealBreakDuration?: number | null;
  policy: TimesheetMissingBreakPolicyConfig;
}

/**
 * Read only the server's validated APPROVED correction overlay. Its effective
 * minutes supersede the original punch/roster provenance, including zero.
 * This is evidence consumption, not correction validation or approval.
 */
export function approvedTimesheetCorrectionBreakMinutes(input: {
  storedBreakMinutes: number | null | undefined;
  sourceEvidence?: unknown;
}): number | null {
  const evidence = input.sourceEvidence;
  if (!evidence || typeof evidence !== 'object' || Array.isArray(evidence)) return null;
  const correction = (evidence as Record<string, unknown>).approvedTimesheetCorrection;
  if (!correction || typeof correction !== 'object' || Array.isArray(correction)) return null;
  const marker = correction as Record<string, unknown>;
  return marker.kind === 'TIME_BREAK' &&
    typeof marker.id === 'string' &&
    marker.id.trim().length > 0 &&
    typeof input.storedBreakMinutes === 'number' &&
    Number.isInteger(input.storedBreakMinutes) &&
    input.storedBreakMinutes >= 0
    ? input.storedBreakMinutes
    : null;
}

/**
 * Convert the timesheet's legacy non-null `break_minutes` into the resolver's
 * missing-versus-explicit contract.
 *
 * The table historically stored both "not captured" and "explicitly no break"
 * as zero. Independently approved no-break facts, coordinator entries and
 * server-attributed officer zero suppress an active provider assumption. Positive
 * recorded minutes remain authoritative. When neither exists, an active
 * provider policy returns `undefined`, allowing the applicable rule block to
 * supply its own default duration and treatment.
 */
export function resolveTimesheetActualBreakInput(input: ResolveTimesheetActualBreakInput): {
  actualBreakMinutes: number | undefined;
  provenance: TimesheetBreakMinutesProvenance;
  policyApplied: boolean;
} {
  const correctedMinutes = approvedTimesheetCorrectionBreakMinutes(input);
  if (correctedMinutes !== null) {
    // The correction is newer evidence than facts approved on the source row.
    return {
      actualBreakMinutes: correctedMinutes,
      provenance: 'RECORDED_BREAK_MINUTES',
      policyApplied: false,
    };
  }
  const evidence =
    input.sourceEvidence && typeof input.sourceEvidence === 'object'
      ? (input.sourceEvidence as Record<string, unknown>)
      : {};
  const explicitEntry =
    evidence.break_source === 'WORKER' ||
    evidence.breakEntryProvenance === 'CONFIRMED_EXPLICIT_ZERO' ||
    evidence.breakEntryProvenance === 'ENTERED';
  const unresolved =
    !explicitEntry &&
    (evidence.break_source === 'UNRESOLVED' ||
      evidence.break_minutes_authority === 'UNRESOLVED' ||
      input.storedBreakMinutes == null);
  const storedBreakMinutes = unresolved ? 0 : nonNegativeMinutes(input.storedBreakMinutes, 0);
  const approvedDuration =
    typeof input.approvedMealBreakDuration === 'number' &&
    Number.isFinite(input.approvedMealBreakDuration)
      ? nonNegativeMinutes(input.approvedMealBreakDuration, 0)
      : null;

  if (input.approvedMealBreakTaken === false) {
    return {
      actualBreakMinutes: 0,
      provenance: 'APPROVED_FACT_NOT_TAKEN',
      policyApplied: false,
    };
  }
  if (input.approvedMealBreakTaken === true) {
    if (approvedDuration !== null && approvedDuration > 0) {
      return {
        actualBreakMinutes: approvedDuration,
        provenance: 'APPROVED_FACT_DURATION',
        policyApplied: false,
      };
    }
    if (storedBreakMinutes > 0) {
      return {
        actualBreakMinutes: storedBreakMinutes,
        provenance: 'RECORDED_BREAK_MINUTES',
        policyApplied: false,
      };
    }
    return {
      actualBreakMinutes: undefined,
      provenance: 'APPROVED_FACT_TAKEN_RULE_DEFAULT',
      policyApplied: true,
    };
  }
  if (approvedDuration !== null) {
    return {
      actualBreakMinutes: approvedDuration,
      provenance: 'APPROVED_FACT_DURATION',
      policyApplied: false,
    };
  }
  if (storedBreakMinutes > 0) {
    return {
      actualBreakMinutes: storedBreakMinutes,
      provenance: 'RECORDED_BREAK_MINUTES',
      policyApplied: false,
    };
  }
  if (explicitEntry) {
    return {
      actualBreakMinutes: storedBreakMinutes,
      provenance: 'RECORDED_BREAK_MINUTES',
      policyApplied: false,
    };
  }
  if (unresolved) {
    return { actualBreakMinutes: undefined, provenance: 'UNRESOLVED', policyApplied: false };
  }
  if (timesheetMissingBreakPolicyApplies(input.policy, input.eventDate)) {
    return {
      actualBreakMinutes: undefined,
      provenance: 'PROVIDER_POLICY_RULE_DEFAULT',
      policyApplied: true,
    };
  }
  return {
    actualBreakMinutes: 0,
    provenance: 'LEGACY_RECORDED_ZERO',
    policyApplied: false,
  };
}

function isExplicitZeroMinutes(value: unknown): boolean {
  const minutes =
    typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(minutes) && minutes === 0;
}

function boolParam(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    if (value.trim().toLowerCase() === 'true') return true;
    if (value.trim().toLowerCase() === 'false') return false;
  }
  return fallback;
}

function sourceMatches(
  params: Record<string, unknown>,
  sourceKind: BreakTreatmentSourceKind,
): boolean {
  const configured = params.sourceKinds;
  if (!Array.isArray(configured) || configured.length === 0) return true;
  return configured.map((s) => normalizedUpper(s)).includes(sourceKind);
}

function categoryMatches(
  params: Record<string, unknown>,
  employeeCategory: string | null,
): boolean {
  const configured = params.employeeCategories ?? params.employeeCategory;
  if (isCategoryScoped(params) && canonicalEmployeeCategory(employeeCategory) === null) {
    return false;
  }
  if (!Array.isArray(configured)) {
    const single = canonicalEmployeeCategory(configured) ?? normalizedUpper(configured);
    const supplied =
      canonicalEmployeeCategory(employeeCategory) ?? normalizedUpper(employeeCategory);
    return !single || single === supplied;
  }
  if (configured.length === 0) return true;
  const supplied = canonicalEmployeeCategory(employeeCategory) ?? normalizedUpper(employeeCategory);
  return configured
    .map((value) => canonicalEmployeeCategory(value) ?? normalizedUpper(value))
    .includes(supplied);
}

function isCategoryScoped(params: Record<string, unknown>): boolean {
  const configured = params.employeeCategories ?? params.employeeCategory;
  return Array.isArray(configured) ? configured.length > 0 : normalizedUpper(configured) !== null;
}

function factValue(facts: BreakTreatmentFactInput[] | null | undefined, factCode: string): unknown {
  const targetCode = normalizedUpper(factCode);
  if (!targetCode) return undefined;
  return (facts ?? []).find(
    (fact) =>
      normalizedUpper(fact.status ?? 'SUPPLIED') !== 'REJECTED' &&
      normalizedUpper(fact.factCode) === targetCode,
  )?.value;
}

function isPositiveFactValue(value: unknown): boolean {
  if (value === true) return true;
  if (typeof value === 'number') return Number.isFinite(value) && value > 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toUpperCase();
    return (
      normalized === 'TRUE' || normalized === 'YES' || normalized === 'Y' || normalized === '1'
    );
  }
  return false;
}

function requiredPositiveFactCodes(params: Record<string, unknown>): string[] {
  const configured = params.requiredPositiveFactCodes ?? params.required_positive_fact_codes;
  if (!Array.isArray(configured)) return [];
  return Array.from(
    new Set(
      configured
        .map((code) => normalizedUpper(code))
        .filter((code): code is string => Boolean(code)),
    ),
  ).sort();
}

function blockIsActive(block: BreakTreatmentRuleBlock): boolean {
  const status = normalizedUpper(block.automation_status ?? 'AUTOMATED');
  return status === 'AUTOMATED' || status === 'MANUAL_ONLY';
}

function blockTreatmentKind(block: BreakTreatmentRuleBlock): BreakTreatmentKind | null {
  const params = block.parameters ?? {};
  const explicit = normalizedUpper(params.treatmentKind ?? params.breakTreatmentKind);
  if (
    explicit === 'UNPAID' ||
    explicit === 'PAID_COUNTS_AS_TIME_WORKED' ||
    explicit === 'PAID_NOT_TIME_WORKED' ||
    explicit === 'NO_BREAK'
  ) {
    return explicit;
  }

  // Only explicit break-treatment blocks may affect payroll break handling.
  // Ordinary-hours blocks can carry roster/contract parameters such as
  // paidHoursPerDay or unpaidMealBreakMinutes, but those are not a source of
  // final break treatment for a worked timesheet.
  if (normalizedUpper(block.block_kind) !== 'BREAK_TREATMENT') return null;
  if (params.paidMealBreakAfterHours !== undefined) return 'PAID_COUNTS_AS_TIME_WORKED';
  if (params.unpaidMealBreakMinutes !== undefined) return 'UNPAID';
  return null;
}

function chooseRuleBlock(
  input: ResolveBreakTreatmentInput,
  scheduledBreakMinutes: number,
): {
  block: BreakTreatmentRuleBlock | null;
  treatmentKind: BreakTreatmentKind;
  scopedCategoryResolutionRequired: boolean;
  belowThresholdRule: BreakTreatmentRuleBlock | null;
} {
  const blocks = (input.ruleBlocks ?? []).filter(blockIsActive);
  const sourceAndTriggerCandidates = blocks
    .map((block) => {
      const params = block.parameters ?? {};
      const treatmentKind = blockTreatmentKind(block);
      if (!treatmentKind) return null;
      if (!sourceMatches(params, input.sourceKind)) return null;
      const requiresExplicitZeroBreak = boolParam(
        params.requiresExplicitZeroBreak ?? params.requires_explicit_zero_break,
        false,
      );
      const hasExplicitZeroBreak =
        input.actualBreakMinutes !== null &&
        input.actualBreakMinutes !== undefined &&
        isExplicitZeroMinutes(input.actualBreakMinutes);
      if (requiresExplicitZeroBreak && !hasExplicitZeroBreak) return null;
      const triggerAfterMinutes = nonNegativeMinutes(
        params.triggerAfterContinuousMinutes ??
          (params.paidMealBreakAfterHours !== undefined
            ? Number(params.paidMealBreakAfterHours) * 60
            : undefined),
        0,
      );
      if (triggerAfterMinutes > 0 && input.rawSpanMinutes <= triggerAfterMinutes) return null;
      const defaultBreakMinutes = nonNegativeMinutes(
        params.defaultBreakMinutes ?? params.paidMealBreakMinutes ?? params.unpaidMealBreakMinutes,
        scheduledBreakMinutes,
      );
      return {
        block,
        treatmentKind,
        // Mirrors the DB default for pay_rule_blocks.precedence: omitted values
        // behave like a broad/default rule, while lower configured values win.
        precedence: nonNegativeNumber(block.precedence, 100),
        specificity:
          (requiresExplicitZeroBreak ? 1_000 : 0) +
          (normalizedUpper(block.block_kind) === 'BREAK_TREATMENT' ? 100 : 0) +
          (Array.isArray(params.employeeCategories) || params.employeeCategory ? 10 : 0) +
          (requiredPositiveFactCodes(params).length > 0 ? 5 : 0) +
          (defaultBreakMinutes > 0 ? 1 : 0),
      };
    })
    .filter(
      (
        entry,
      ): entry is {
        block: BreakTreatmentRuleBlock;
        treatmentKind: BreakTreatmentKind;
        specificity: number;
        precedence: number;
      } => Boolean(entry),
    );

  // A rule that otherwise applies must remain visible when its required
  // positive fact is absent/false. Filtering it out here used to silently
  // fall back to ordinary unpaid-break semantics during FINAL payroll.
  const candidates = sourceAndTriggerCandidates
    .filter((entry) =>
      categoryMatches(entry.block.parameters ?? {}, input.employeeCategory ?? null),
    )
    .sort(
      (a, b) =>
        b.specificity - a.specificity ||
        a.precedence - b.precedence ||
        a.block.block_code.localeCompare(b.block.block_code),
    );

  const suppliedCategory = canonicalEmployeeCategory(input.employeeCategory);
  const scopedCategoryResolutionRequired =
    suppliedCategory === null &&
    sourceAndTriggerCandidates.some((entry) => isCategoryScoped(entry.block.parameters ?? {}));

  const bestCandidate = candidates[0];
  if (bestCandidate) {
    return {
      block: bestCandidate.block,
      treatmentKind: bestCandidate.treatmentKind,
      scopedCategoryResolutionRequired,
      belowThresholdRule: null,
    };
  }

  return {
    block: null,
    treatmentKind: scheduledBreakMinutes > 0 ? 'UNPAID' : 'NO_BREAK',
    scopedCategoryResolutionRequired,
    // A known rule with a later trigger establishes that no break is due. An
    // empty rule set, unknown category or missing roster alone cannot do that.
    belowThresholdRule:
      !scopedCategoryResolutionRequired && input.rawSpanMinutes > 0
        ? (blocks
            .slice()
            .sort((a, b) => a.block_code.localeCompare(b.block_code))
            .find((candidate) => {
              const params = candidate.parameters ?? {};
              const threshold = nonNegativeMinutes(
                params.triggerAfterContinuousMinutes ??
                  (params.paidMealBreakAfterHours !== undefined
                    ? Number(params.paidMealBreakAfterHours) * 60
                    : undefined),
                0,
              );
              return (
                blockTreatmentKind(candidate) !== null &&
                !boolParam(
                  params.requiresExplicitZeroBreak ?? params.requires_explicit_zero_break,
                  false,
                ) &&
                sourceMatches(params, input.sourceKind) &&
                categoryMatches(params, input.employeeCategory ?? null) &&
                threshold > 0 &&
                input.rawSpanMinutes <= threshold
              );
            }) ?? null)
        : null,
  };
}

function suppliedFactCodes(facts: BreakTreatmentFactInput[] | null | undefined): string[] {
  return Array.from(
    new Set(
      (facts ?? [])
        .filter((fact) => normalizedUpper(fact.status ?? 'SUPPLIED') !== 'REJECTED')
        .map((fact) => normalizedUpper(fact.factCode))
        .filter((code): code is string => Boolean(code)),
    ),
  ).sort();
}

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map((item) => stableStringify(item)).join(',')}]`;
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(record[key])}`)
    .join(',')}}`;
}

function stableRuleHash(payload: unknown): string {
  // Change detector only: audit evidence persists the full snapshot, not this
  // small non-cryptographic hash as a content-address or tamper-proof digest.
  const text = stableStringify(payload);
  let hash = 0xcbf29ce484222325n;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= BigInt(text.charCodeAt(i));
    hash = BigInt.asUintN(64, hash * 0x100000001b3n);
  }
  return hash.toString(16).padStart(16, '0');
}

function canonicalFacts(
  facts: BreakTreatmentFactInput[] | null | undefined,
): Array<{ factCode: string; status: string; value: unknown }> {
  return (facts ?? [])
    .map((fact) => ({
      factCode: normalizedUpper(fact.factCode) ?? '',
      status: normalizedUpper(fact.status ?? 'SUPPLIED') ?? 'SUPPLIED',
      value: fact.value === undefined ? null : fact.value,
    }))
    .sort(
      (a, b) =>
        a.factCode.localeCompare(b.factCode) ||
        a.status.localeCompare(b.status) ||
        stableStringify(a.value).localeCompare(stableStringify(b.value)),
    );
}

/**
 * Is this worker paid a flat salary rather than against the award?
 *
 * Deliberately a local, literal check rather than an import: this package must not
 * depend on the web app, and the token is a stored enum value, not a computed one.
 */
function isSalaryFlatPaymentMethodToken(value: unknown): boolean {
  return normalizedUpper(value) === 'SALARY_FLAT';
}

export function resolveBreakTreatment(input: ResolveBreakTreatmentInput): BreakTreatmentSnapshotV1 {
  const rawSpanMinutes = nonNegativeMinutes(input.rawSpanMinutes, 0);
  const salaryFlat = isSalaryFlatPaymentMethodToken(input.paymentMethod);
  const noTenantRuleRecordedZero =
    input.sourceKind === 'TIMESHEET' &&
    input.noTenantBreakRule === true &&
    input.recordedBreakMinutes === 0;

  // Salary-flat short-circuit.
  //
  // Returns BEFORE any rule-block selection, and builds its own rule hash rather
  // than adding `paymentMethod` to the shared one. That is deliberate: folding a
  // new field into the common hash would change the hash of EVERY existing
  // snapshot, marking every stored row stale at once and forcing a mass
  // re-resolution across every tenant. Award rows keep byte-identical hashes.
  //
  // The hash still varies with the inputs that matter here, so a worker converting
  // off flat salary produces a different hash and the row is correctly re-resolved
  // rather than silently keeping a NOT_APPLICABLE it no longer deserves.
  if (salaryFlat || noTenantRuleRecordedZero) {
    return {
      schemaVersion: 1,
      status: 'NOT_APPLICABLE',
      treatmentKind: 'NO_BREAK',
      sourceKind: input.sourceKind,
      ruleBlockId: null,
      ruleBlockCode: null,
      ruleHash: stableRuleHash({
        resolverVersion: `${BREAK_TREATMENT_RESOLVER_HASH_VERSION}+${salaryFlat ? 'salary-flat' : 'no-tenant-break-rule'}`,
        sourceKind: input.sourceKind,
        ...(salaryFlat ? { paymentMethod: 'SALARY_FLAT' } : { recordedBreakMinutes: 0 }),
        rawSpanMinutes,
        finalizationMode: input.finalizationMode ?? 'FORECAST',
      }),
      scheduledBreakMinutes: 0,
      actualBreakMinutes: 0,
      ...(noTenantRuleRecordedZero && !salaryFlat
        ? {
            actualBreakMinutesProvenance:
              input.actualBreakMinutes === 0 && input.actualBreakMinutesProvenance !== 'UNRESOLVED'
                ? (input.actualBreakMinutesProvenance ?? 'NONE')
                : 'NONE',
          }
        : {}),
      rawSpanMinutes,
      workedMinutes: rawSpanMinutes,
      payrollPaidBreakMinutes: 0,
      payrollDeductibleBreakMinutes: 0,
      ordinaryTimeWorkedBreakMinutes: 0,
      leaveAccruableBreakMinutes: 0,
      overtimeThresholdBreakMinutes: 0,
      careMinuteExcludedBreakMinutes: 0,
      requiredFactCodes: [],
      suppliedFactCodes: salaryFlat ? [] : suppliedFactCodes(input.facts),
      ...(!salaryFlat ? { derivedFacts: [] } : {}),
      missingFactCodes: [],
      blockers: [],
      segments: salaryFlat
        ? []
        : [
            {
              segmentKind: 'WORKED',
              minutes: rawSpanMinutes,
              countsForPayroll: true,
              countsAsTimeWorked: true,
              countsForLeave: true,
              countsForOvertimeThreshold: true,
              countsForCareMinutes: true,
            },
          ],
    };
  }

  const capturedScheduledBreakMinutes = nonNegativeMinutes(input.scheduledBreakMinutes, 0);
  const actualBreakMinutesProvided =
    input.actualBreakMinutes !== null && input.actualBreakMinutes !== undefined;
  const capturedActualBreakMinutes = nonNegativeMinutes(
    input.actualBreakMinutes,
    capturedScheduledBreakMinutes,
  );
  const { block, treatmentKind, scopedCategoryResolutionRequired, belowThresholdRule } =
    chooseRuleBlock(input, capturedScheduledBreakMinutes);
  const params = block?.parameters ?? {};
  const configuredDefaultBreakMinutes = block
    ? nonNegativeMinutes(
        params.defaultBreakMinutes ?? params.paidMealBreakMinutes ?? params.unpaidMealBreakMinutes,
        0,
      )
    : 0;
  const shouldApplyConfiguredDefault =
    treatmentKind !== 'NO_BREAK' &&
    configuredDefaultBreakMinutes > 0 &&
    !actualBreakMinutesProvided &&
    capturedScheduledBreakMinutes === 0 &&
    capturedActualBreakMinutes === 0 &&
    // An unknown unpaid break needs actual duration evidence. Paid breaks
    // remain assumed taken under the applicable rule; no second declaration.
    (input.actualBreakMinutesProvenance !== 'UNRESOLVED' ||
      treatmentKind === 'PAID_COUNTS_AS_TIME_WORKED' ||
      treatmentKind === 'PAID_NOT_TIME_WORKED');
  const defaultBreakMinutes =
    shouldApplyConfiguredDefault && rawSpanMinutes > 0
      ? Math.min(configuredDefaultBreakMinutes, rawSpanMinutes)
      : configuredDefaultBreakMinutes;
  const scheduledBreakMinutes = shouldApplyConfiguredDefault
    ? defaultBreakMinutes
    : capturedScheduledBreakMinutes;
  const actualBreakMinutes = shouldApplyConfiguredDefault
    ? defaultBreakMinutes
    : capturedActualBreakMinutes;
  const noBreakRequired =
    belowThresholdRule !== null &&
    treatmentKind === 'NO_BREAK' &&
    actualBreakMinutes === 0 &&
    input.actualBreakMinutesProvenance === 'UNRESOLVED';
  const actualBreakMinutesProvenance: TimesheetBreakMinutesProvenance = noBreakRequired
    ? 'RULE_NO_BREAK_REQUIRED'
    : shouldApplyConfiguredDefault
      ? (input.actualBreakMinutesProvenance ?? 'RESOLVER_RULE_DEFAULT')
      : actualBreakMinutesProvided
        ? (input.actualBreakMinutesProvenance ?? 'RECORDED_BREAK_MINUTES')
        : capturedScheduledBreakMinutes > 0
          ? 'SCHEDULED_BREAK_MINUTES'
          : input.actualBreakMinutesProvenance === 'UNRESOLVED'
            ? 'UNRESOLVED'
            : 'NONE';
  const facts = suppliedFactCodes(input.facts);
  const positiveFactCodes = requiredPositiveFactCodes(params);
  const paidLunchDerivationProvenance:
    | NonNullable<BreakTreatmentSnapshotV1['derivedFacts']>[number]['provenance']
    | null =
    actualBreakMinutesProvided && capturedActualBreakMinutes > 0
      ? 'DERIVED_FROM_RECORDED_BREAK_MINUTES'
      : shouldApplyConfiguredDefault &&
          actualBreakMinutes > 0 &&
          actualBreakMinutesProvenance === 'PROVIDER_POLICY_RULE_DEFAULT'
        ? 'DERIVED_FROM_PROVIDER_MISSING_BREAK_POLICY'
        : shouldApplyConfiguredDefault &&
            actualBreakMinutes > 0 &&
            actualBreakMinutesProvenance === 'APPROVED_FACT_TAKEN_RULE_DEFAULT'
          ? 'DERIVED_FROM_APPROVED_BREAK_TAKEN_FACT'
          : shouldApplyConfiguredDefault &&
              actualBreakMinutes > 0 &&
              input.actualBreakMinutesProvenance === 'UNRESOLVED' &&
              (treatmentKind === 'PAID_COUNTS_AS_TIME_WORKED' ||
                treatmentKind === 'PAID_NOT_TIME_WORKED')
            ? 'DERIVED_FROM_RULE_PAID_BREAK_ASSUMPTION'
            : null;
  const derivedFacts: NonNullable<BreakTreatmentSnapshotV1['derivedFacts']> =
    paidLunchDerivationProvenance &&
    positiveFactCodes.includes('PAID_LUNCH_BREAK') &&
    factValue(input.facts, 'PAID_LUNCH_BREAK') === undefined
      ? [
          {
            factCode: 'PAID_LUNCH_BREAK',
            provenance: paidLunchDerivationProvenance,
          },
        ]
      : [];
  const derivedFactCodes = new Set(derivedFacts.map((fact) => fact.factCode));
  const requiredFactCodes = Array.from(
    new Set(
      [
        ...(block?.required_fact_codes ?? []),
        ...((Array.isArray(params.requiredFactCodes) ? params.requiredFactCodes : []) as unknown[]),
        ...requiredPositiveFactCodes(params),
      ]
        .map((code) => normalizedUpper(code))
        .filter((code): code is string => Boolean(code)),
    ),
  ).sort();

  const missingFactCodes =
    input.finalizationMode === 'FINAL'
      ? requiredFactCodes.filter((code) => !facts.includes(code) && !derivedFactCodes.has(code))
      : [];
  const unsatisfiedPositiveFactCodes = positiveFactCodes.filter(
    (code) => !derivedFactCodes.has(code) && !isPositiveFactValue(factValue(input.facts, code)),
  );

  const blockers: BreakTreatmentSnapshotV1['blockers'] = [];
  const breakMinutesUnresolved =
    (input.actualBreakMinutesProvenance === 'UNRESOLVED' &&
      actualBreakMinutes === 0 &&
      !noBreakRequired) ||
    (input.actualBreakMinutesProvenance === 'APPROVED_FACT_TAKEN_RULE_DEFAULT' &&
      capturedScheduledBreakMinutes === 0 &&
      !actualBreakMinutesProvided);
  if (breakMinutesUnresolved) {
    blockers.push({
      code: 'BREAK_DURATION_UNRESOLVED',
      message:
        'Break length is unknown. Use Correct actual time to enter the break minutes or confirm that no meal break was taken.',
    });
  }
  if (missingFactCodes.length > 0) {
    blockers.push({
      code: 'BREAK_TREATMENT_FACTS_MISSING',
      message: `Required break-treatment facts missing: ${missingFactCodes.join(', ')}.`,
    });
  }
  if (input.finalizationMode === 'FINAL' && unsatisfiedPositiveFactCodes.length > 0) {
    blockers.push({
      code: 'BREAK_TREATMENT_POSITIVE_FACT_REQUIRED',
      message: `Break-treatment facts must be explicitly positive: ${unsatisfiedPositiveFactCodes.join(', ')}.`,
    });
  }
  if (input.finalizationMode === 'FINAL' && scopedCategoryResolutionRequired) {
    blockers.push({
      code: 'BREAK_TREATMENT_EMPLOYEE_CATEGORY_UNRESOLVED',
      message:
        'Employee category is missing or unrecognised while category-scoped break rules apply.',
    });
  }
  const countsAsTimeWorked =
    treatmentKind === 'PAID_COUNTS_AS_TIME_WORKED' || boolParam(params.countsAsTimeWorked, false);
  const countsForLeave =
    treatmentKind === 'PAID_COUNTS_AS_TIME_WORKED' || boolParam(params.countsForLeave, false);
  const countsForOvertimeThreshold =
    treatmentKind === 'PAID_COUNTS_AS_TIME_WORKED' ||
    boolParam(params.countsForOvertimeThreshold, countsAsTimeWorked);

  const payrollPaidBreakMinutes =
    treatmentKind === 'PAID_COUNTS_AS_TIME_WORKED' || treatmentKind === 'PAID_NOT_TIME_WORKED'
      ? actualBreakMinutes
      : 0;
  const payrollDeductibleBreakMinutes = treatmentKind === 'UNPAID' ? actualBreakMinutes : 0;
  const workedMinutes = Math.max(0, rawSpanMinutes - actualBreakMinutes);
  const paidBreakSegment: BreakTreatmentSegment | null =
    payrollPaidBreakMinutes > 0
      ? {
          segmentKind:
            treatmentKind === 'PAID_COUNTS_AS_TIME_WORKED'
              ? 'PAID_BREAK_COUNTS_AS_TIME_WORKED'
              : 'PAID_BREAK_NOT_TIME_WORKED',
          minutes: payrollPaidBreakMinutes,
          countsForPayroll: true,
          countsAsTimeWorked,
          countsForLeave,
          countsForOvertimeThreshold,
          countsForCareMinutes: false,
        }
      : null;
  const unpaidBreakSegment: BreakTreatmentSegment | null =
    payrollDeductibleBreakMinutes > 0
      ? {
          segmentKind: 'UNPAID_BREAK',
          minutes: payrollDeductibleBreakMinutes,
          countsForPayroll: false,
          countsAsTimeWorked: false,
          countsForLeave: false,
          countsForOvertimeThreshold: false,
          countsForCareMinutes: false,
        }
      : null;

  return {
    schemaVersion: 1,
    status:
      blockers.length > 0
        ? 'BLOCKED'
        : input.finalizationMode !== 'FINAL' &&
            (unsatisfiedPositiveFactCodes.length > 0 || scopedCategoryResolutionRequired)
          ? 'AMBIGUOUS'
          : scheduledBreakMinutes > 0 || actualBreakMinutes > 0
            ? 'RESOLVED'
            : 'NOT_APPLICABLE',
    treatmentKind,
    sourceKind: input.sourceKind,
    ruleBlockId: block?.id ?? null,
    ruleBlockCode: block?.block_code ?? null,
    ...(noBreakRequired ? { noBreakRequiredRuleBlockId: belowThresholdRule?.id ?? null } : {}),
    ruleHash: stableRuleHash({
      resolverVersion: BREAK_TREATMENT_RESOLVER_HASH_VERSION,
      sourceKind: input.sourceKind,
      employeeCategory:
        canonicalEmployeeCategory(input.employeeCategory) ??
        normalizedUpper(input.employeeCategory),
      rawSpanMinutes,
      capturedScheduledBreakMinutes,
      actualBreakMinutesProvided,
      actualBreakMinutes,
      ...(input.actualBreakMinutesProvenance && actualBreakMinutesProvenance !== 'NONE'
        ? { actualBreakMinutesProvenance }
        : {}),
      finalizationMode: input.finalizationMode ?? 'FORECAST',
      facts: canonicalFacts(input.facts),
      block: block
        ? {
            id: block.id ?? null,
            blockCode: block.block_code,
            blockKind: block.block_kind,
            automationStatus: block.automation_status ?? null,
            precedence: block.precedence ?? null,
            parameters: params,
            requiredFactCodes: block.required_fact_codes ?? [],
            exportComponentCodes: block.export_component_codes ?? [],
          }
        : null,
      treatmentKind,
      requiredFactCodes,
      derivedFacts,
      missingFactCodes,
      unsatisfiedPositiveFactCodes,
      scopedCategoryResolutionRequired,
      ...(noBreakRequired ? { belowThresholdRule } : {}),
    }),
    scheduledBreakMinutes,
    actualBreakMinutes,
    actualBreakMinutesProvenance,
    ...(breakMinutesUnresolved ? { breakMinutesStatus: 'UNRESOLVED' as const } : {}),
    rawSpanMinutes,
    workedMinutes,
    payrollPaidBreakMinutes,
    payrollDeductibleBreakMinutes,
    ordinaryTimeWorkedBreakMinutes: countsAsTimeWorked ? actualBreakMinutes : 0,
    leaveAccruableBreakMinutes: countsForLeave ? actualBreakMinutes : 0,
    overtimeThresholdBreakMinutes: countsForOvertimeThreshold ? actualBreakMinutes : 0,
    careMinuteExcludedBreakMinutes: actualBreakMinutes,
    requiredFactCodes,
    suppliedFactCodes: facts,
    derivedFacts,
    missingFactCodes,
    blockers,
    segments: [
      {
        segmentKind: 'WORKED',
        minutes: workedMinutes,
        countsForPayroll: true,
        countsAsTimeWorked: true,
        countsForLeave: true,
        countsForOvertimeThreshold: true,
        countsForCareMinutes: true,
      },
      ...(paidBreakSegment ? [paidBreakSegment] : []),
      ...(unpaidBreakSegment ? [unpaidBreakSegment] : []),
    ],
  };
}

export function payrollBreakMinutesForEngine(
  rawBreakMinutes: number | null | undefined,
  breakTreatment: BreakTreatmentSnapshotV1 | null | undefined,
): number {
  if (!breakTreatment) return nonNegativeMinutes(rawBreakMinutes, 0);
  if (breakTreatment.status === 'BLOCKED') return nonNegativeMinutes(rawBreakMinutes, 0);
  if (breakTreatment.treatmentKind === 'PAID_NOT_TIME_WORKED') {
    return breakTreatment.payrollDeductibleBreakMinutes + breakTreatment.payrollPaidBreakMinutes;
  }
  return breakTreatment.payrollDeductibleBreakMinutes;
}
